import {
  faReceipt,
  faCreditCard,
  faDragon,
  faMessage,
  faVideo,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  Button,
  CloseCircleIcon,
  FlexBox,
  JoonColorExpanded,
  JoonUIColor,
  SPACING,
  TextButton,
  Typography,
} from "@joonapp/web-shared"
import { useTranslation } from "react-i18next"
import { create } from "zustand"

import ChatBubbleDownRight from "./ChatBubbleDownRight"
import { PlanTitle } from "./PlanTitle"
import { usePlanCard } from "./usePlanCard"
import { useWelcomeModalStore } from "../../../../components/modals/WelcomeModal"
import SlideUpModal from "../../../../components/slideUpModal/SlideUpModal"
import { COACH_PLANS, CoachPaywallChoice } from "../../../../constants"
import useMobile from "../../../../hooks/useMobile"
import { useCoachSubscriptionPackagesQuery } from "../../../../networking/coaching/queries"
import {
  CoachingLookupKey,
  CoachingStep,
} from "../../../../types/coachingGroups"
import { SubscriptionPackage } from "../../../../types/user"
import { ANALYTIC_EVENTS } from "../../../../util/analytics"
import { trackAnalyticEvent } from "../../../../util/analytics"
import { useRouter } from "../../../../util/router"
import { localStorageItems } from "../../../../util/storage"
import { formattedCentsToDollars } from "../../../../util/util"
import { useCoachingStore } from "../../useCoachingStore"
import useIsFromMOF from "../../useIsFromMOF"

interface PlanSecondaryOfferingModalStore {
  isOpen: boolean
  onOpen: () => void
  onClose: () => void
}

export const usePlanSecondaryOfferingModalStore =
  create<PlanSecondaryOfferingModalStore>((set) => ({
    isOpen: false,
    onOpen: () => set({ isOpen: true }),
    onClose: () => set({ isOpen: false }),
  }))

const PlanSecondaryOfferingModal = () => {
  const { selectedCoach } = useCoachingStore()
  const { isOpen, onClose } = usePlanSecondaryOfferingModalStore()
  const isMobileMediaQuery = useMobile()
  const router = useRouter()
  const isFromMOF = useIsFromMOF()
  const { onOpen: openWelcomeModal } = useWelcomeModalStore()
  const { t } = useTranslation()

  const onNoThankYouClick = () => {
    onClose()
    trackAnalyticEvent(ANALYTIC_EVENTS.MARKETING_SKIP_CLICK, {
      step: CoachingStep.PLAN_SELECT,
      is_tof: !isFromMOF,
    })
    if (
      localStorage.getItem(localStorageItems.planChoiceFromPaywall) ===
        CoachPaywallChoice.WITH_COACH ||
      localStorage.getItem(localStorageItems.planChoiceFromPaywall) ===
        CoachPaywallChoice.EXIT_WITH_COACH
    ) {
      router.push("/paywall/trial")
    } else {
      localStorage.removeItem(localStorageItems.planChoiceFromPaywall)
      router.push("/me")
      if (isFromMOF) return

      openWelcomeModal()
    }
  }

  return (
    <SlideUpModal
      isOpen={isOpen}
      onClose={onClose}
      size="fit-content"
      backgroundType="overlay"
    >
      <TextButton
        onClick={onClose}
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          padding: SPACING.space2,
          borderRadius: "50%",
        }}
      >
        <CloseCircleIcon size={16} color={JoonUIColor.icon.neutral} />
      </TextButton>

      <FlexBox
        direction="column"
        align="center"
        gap={SPACING.space2}
        style={{
          padding: SPACING.space6,
          paddingTop: isMobileMediaQuery ? "120px" : "140px",
        }}
      >
        <ChatBubbleDownRight
          text={t("COACHING_SECONDARY_NOT_READY")}
          boldText={t("COACHING_SECONDARY_STARTER")}
          imageUrl={selectedCoach?.profile_image_url || ""}
        />
        <PlanSecondaryOfferingModalCard />
        <TextButton onClick={onNoThankYouClick}>
          <Typography variant="bodyBold" color={JoonUIColor.text.primaryAccent}>
            {t("COACHING_SECONDARY_NO_THANK_YOU")}
          </Typography>
        </TextButton>
      </FlexBox>
    </SlideUpModal>
  )
}

const PlanSecondaryOfferingModalCard = () => {
  const { t } = useTranslation()
  const { selectedCoach } = useCoachingStore()
  const { onClose } = usePlanSecondaryOfferingModalStore()
  const { data: subscriptionPackages } = useCoachSubscriptionPackagesQuery()
  const { redirectToCoachCheckout, isLoading } = usePlanCard()
  const starterPlan: SubscriptionPackage | undefined = Object.values(
    subscriptionPackages || {}
  ).find((plan) => plan.starter_plan && plan.billing_period_months === 1)
  const starterLookupKey =
    starterPlan?.lookup_key || CoachingLookupKey.TEXT_ONLY
  const { plan } = COACH_PLANS[starterLookupKey]
  const isMobileMediaQuery = useMobile()
  const first_month_discount = starterPlan?.first_month_discount
  const priceInCents = starterPlan?.unit_amount ?? 0
  const numSessions = starterPlan?.num_sessions ?? 0

  const priceForToday = formattedCentsToDollars(
    first_month_discount!.unit_amount
  )
  const fullPrice = formattedCentsToDollars(priceInCents)

  const bundleJoonity =
    localStorage.getItem(localStorageItems.planChoiceFromPaywall) ===
      CoachPaywallChoice.EXIT_WITH_COACH ||
    localStorage.getItem(localStorageItems.planChoiceFromPaywall) ===
      CoachPaywallChoice.WITH_COACH

  const infoCards = [
    ...(bundleJoonity
      ? [
          {
            icon: faDragon,
            text: t("PAYWALL_BUNDLING_FULL_ACCESS_INCLUDED"),
          },
        ]
      : []),
    {
      icon: faMessage,
      text: t("COACHING_SECONDARY_MESSAGES"),
    },
    ...(numSessions > 0
      ? [
          {
            icon: faVideo,
            text: t("COACHING_SECONDARY_SESSIONS", { count: numSessions }),
          },
        ]
      : []),
    {
      icon: faCreditCard,
      text: t("PAYWALL_BUNDLING_HSA"),
    },
    {
      icon: faReceipt,
      text: t("COACHING_SECONDARY_RISK_FREE"),
    },
  ]

  return (
    <FlexBox
      direction="column"
      justify="center"
      align="center"
      gap={SPACING.space0}
      style={{
        width: "min(100%, 450px)",
        paddingBottom: SPACING.space2,
        opacity: subscriptionPackages ? 1 : 0,
        background: "#F9FAFC",
        border: `2px solid ${JoonUIColor.text.primaryAccent}`,
        borderRadius: "16px",
        boxShadow: "0px 4px 10px 0px rgba(0, 0, 0, 0.10)",
        transition: "opacity .3s ease-in-out",
      }}
    >
      <FlexBox
        direction="column"
        align="center"
        gap={SPACING.space2}
        wrap={false}
        style={{ padding: SPACING.space4 }}
      >
        <FlexBox
          direction="row"
          justify="space-between"
          align="center"
          gap={SPACING.space1}
          wrap={false}
          fullWidth
        >
          <PlanTitle plan={plan} coachingPlan={starterLookupKey} />
          {first_month_discount ? (
            <FlexBox
              direction="row"
              justify="flex-end"
              align="center"
              gap={SPACING.space1}
              wrap={false}
            >
              <Typography
                variant={isMobileMediaQuery ? "h3" : "h2"}
                color={JoonUIColor.background.disabled}
                style={{ textDecoration: "line-through" }}
              >
                ${fullPrice}
              </Typography>
              <Typography
                variant={isMobileMediaQuery ? "h3" : "h2"}
                color={JoonUIColor.text.primary}
              >
                ${priceForToday}
              </Typography>
            </FlexBox>
          ) : (
            <FlexBox
              direction="row"
              justify="flex-end"
              align="center"
              gap={SPACING.space1}
              wrap={false}
            >
              <Typography
                variant={isMobileMediaQuery ? "h3" : "h2"}
                color={JoonUIColor.text.primary}
              >
                ${fullPrice}
                <Typography variant="body" color={JoonUIColor.text.primary}>
                  {t("COACHING_PLAN_PER_MONTH")}
                </Typography>
              </Typography>
            </FlexBox>
          )}
        </FlexBox>

        {first_month_discount && (
          <>
            <FlexBox
              style={{
                padding: `${SPACING.space1} ${SPACING.space2}`,
                background: JoonColorExpanded.yellow300,
                borderRadius: SPACING.space1,
              }}
              fullWidth
            >
              <Typography
                variant="bodyXSmallBold"
                color={JoonUIColor.text.primary}
                textAlign="center"
                style={{ whiteSpace: "nowrap" }}
              >
                {t("COACHING_PLAN_FIRST_MONTH", {
                  percent: first_month_discount.percentage_off,
                })}
              </Typography>
            </FlexBox>
            <Typography
              variant="bodyXSmallBold"
              color={JoonUIColor.text.secondary}
            >
              {t("COACHING_PLAN_AFTERWARDS", { price: fullPrice })}
            </Typography>
          </>
        )}
      </FlexBox>

      <FlexBox
        direction="column"
        align="flex-start"
        gap={SPACING.space2}
        wrap={false}
        style={{
          padding: `${SPACING.space4} ${SPACING.space2}`,
          background: JoonUIColor.background.primaryNeutral,
          borderTop: `1px solid ${JoonUIColor.icon.light}`,
          borderBottom: `1px solid ${JoonUIColor.icon.light}`,
        }}
      >
        {infoCards.map(({ icon, text }, index) => (
          <FlexBox
            key={index}
            direction="row"
            justify="center"
            align="center"
            gap={SPACING.space2}
            wrap={false}
            style={{ width: "fit-content" }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minWidth: "16px",
                maxWidth: "16px",
                minHeight: "16px",
                maxHeight: "16px",
              }}
            >
              <FontAwesomeIcon
                icon={icon}
                color={JoonUIColor.icon.accent}
                style={{ width: "100%", height: "100%" }}
              />
            </div>
            <Typography
              variant="bodyXSmallBold"
              color={JoonUIColor.text.primary}
              style={{
                textAlign: "left",
                lineHeight: "1.3",
                whiteSpace: "pre-line",
              }}
            >
              {text}
            </Typography>
          </FlexBox>
        ))}
      </FlexBox>

      <div
        style={{
          width: "100%",
          padding: SPACING.space2,
          paddingTop: SPACING.space4,
        }}
      >
        <Button
          fullWidth
          isLoading={isLoading}
          onClick={() =>
            redirectToCoachCheckout(starterLookupKey, priceInCents / 100).then(
              onClose
            )
          }
          style={{
            position: "relative",
            cursor: "pointer",
          }}
          text={
            <Typography
              variant={isMobileMediaQuery ? "bodySmallBold" : "bodyBold"}
              color={JoonUIColor.text.inverted}
            >
              {t("COACHING_PLAN_GET_STARTED_WITH", {
                name: selectedCoach?.user.name || t("COACHING_PLAN_YOUR_COACH"),
              })}
            </Typography>
          }
        />
      </div>
    </FlexBox>
  )
}

export default PlanSecondaryOfferingModal
